/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { ApolloClient, ApolloProvider, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import { useAuth } from 'context/Auth';
import { getTenantId } from 'utils/tenantUtil';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { isTokenExpired, logoutWithRedirect } from 'utils/AuthUtils';
import { ActionButton } from 'components/ActionButton/ActionButton';
import { CustomModal } from 'components/CustomModal';

// Enum for popup types
const POPUP_TYPE = {
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
};

// Config for different popups
const POPUP_CONFIG = {
  [POPUP_TYPE.SESSION_EXPIRED]: {
    title: 'Login timeout.',
    content: 'Bitte loggen Sie sich erneut ein. Ihre Session ist abgelaufen.',
  },
  [POPUP_TYPE.USER_NOT_FOUND]: {
    title: 'Ungültiger Account',
    content: 'Es gibt ein Problem mit Ihrem Account. Bitte loggen Sie sich erneut ein.',
  },
};

const Apollo = ({ children, isPublic }) => {
  const { token, isLoading, isCIAM } = useAuth();
  const [popupType, setPopupType] = useState(null);
  const tenantId = getTenantId();
  let uri;
  if (process.env.REACT_APP_GRAPHQL_ENDPOINT) {
    uri = `${process.env.REACT_APP_GRAPHQL_ENDPOINT}?tenant=${tenantId}`;
  }

  if (!uri) {
    // if this happens the env vars are not set correctly
    return <div>no uri</div>;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }
  const jwtValidation = setContext((_, { headers }) => {
    if ((!token || isTokenExpired(token)) && !isPublic) {
      setPopupType(POPUP_TYPE.SESSION_EXPIRED);
      throw new Error('Invalid or missing token. Request cancelled.');
    }
    return {
      headers,
    };
  });

  // Error handling link
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, extensions }) => {
        if (extensions?.code === 'INTERNAL_SERVER_ERROR' && message === 'User not found') {
          setPopupType(POPUP_TYPE.USER_NOT_FOUND);
        }
      });
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }
  });

  const cache = new InMemoryCache();
  const httpLink = new HttpLink({
    uri: uri,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : []),
    },
  });
  const link = ApolloLink.from([jwtValidation, errorLink, httpLink]);

  const client = new ApolloClient({
    cache,
    link,
  });

  return (
    <ApolloProvider client={client}>
      <>
        {children}

        <CustomModal
          isOpen={!!popupType}
          title={POPUP_CONFIG[popupType]?.title}
          ctaButtons={
            <>
              <ActionButton
                onClick={() => {
                  setPopupType(null);
                  logoutWithRedirect(isCIAM);
                }}
                title="Ok"></ActionButton>
            </>
          }
          content={<>{POPUP_CONFIG[popupType]?.content}</>}></CustomModal>
      </>
    </ApolloProvider>
  );
};

const PUBLIC_NO_AUTH_CONFIG = {
  context: {
    headers: {
      Authorization: '',
    },
  },
};

export { Apollo, PUBLIC_NO_AUTH_CONFIG };
